import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { toast } from 'react-toastify';
import classes from './pipeline.module.scss';
import Api from '../../../../api/api';
import Button from '../../../_common/Form/Buttons/Button';
import Loader from '../../../_common/Loader/Loader';
import Modal from '../../../_common/Modal/Modal';
import AttentionModal from '../../../_common/Modal/AttentionModal/AttentionModal';
import Select from '../../../_common/Form/Selects/Select/Select';
import StatusField from '../../../_common/StatusField/StatusField';
import TableAndPagination from '../../../_common/TableAndPagination/TableAndPagination';
import { RangeDatePicker } from '../../../_common/Form/DatePickers/RangeDatePicker/RangeDatePicker';
import { useQuery } from '../../../../utils/hooks/useQuery';
import Utils from '../../../../utils/utils';
import AccountHelpers from '../../../../utils/accountHelpers';
import { getHealthRequest } from '../../../../store/actions/healths';
import MemberModalContent from './MemberModal/MemberModalContent';
import { formattedDate } from './pipelineHelpers';
import { ReactComponent as EditIcon } from '../../../../assets/icons/edit.svg';
import { ReactComponent as DeleteIcon } from '../../../../assets/icons/delete.svg';
import Chip from '../../../_common/Chip/Chip';

const tableHeader = [
  {
    name: 'Date',
    path: 'date',
    isSelectable: true,
  },
  {
    name: 'Member ID',
    path: 'memberId',
    isSelectable: true,
    render: (d) => d.memberId || '-',
  },
  { name: 'Member Name', path: 'member' },
  { name: 'DOB', path: 'dob', isSelectable: true },
  { name: 'LOB', path: 'lob', isSelectable: true },
  {
    name: 'Source',
    path: 'source',
    isSelectable: true,
  },
  {
    name: 'Status',
    path: 'status',
    isSelectable: true,
    render: (d) => (d.status ? <StatusField status={d.status} /> : '-'),
  },
  { name: 'Actions Needed By', path: 'actionNeededBy' },
  { name: 'Follow-up Date', path: 'followUpDate' },
  {
    name: 'Notes',
    path: 'notes',
    render: (d) => (d.notes
      ? (
        <div className={classes.notes_cell}>
          {d.notes}
        </div>
      )
      : '-'),
  },
  {
    name: 'Final Outcome',
    path: 'finalOutcome',
    isSelectable: true,
    render: (d) => (d.finalOutcome
      ? (
        <div className={classes.chip_wrapper}>
          <Chip text={d.finalOutcome} gray />
        </div>
      )
      : '-'),
  },
  { name: 'Final Outcome Date', path: 'finalOutcomeDate' },
];

const collectRequestData = (requestData) => {
  const data = Utils.deleteEmptyKeys(requestData);

  if (data.start) {
    if (!data.end) {
      data.end = moment(data.start).add(1, 'day').format('YYYY-MM-DD');
    } else {
      data.end = moment(data.end).format('YYYY-MM-DD');
    }
  }

  return data;
};

const thisMonthStartDay = moment().startOf('month').format('YYYY-MM-DD');
const thisMonthEndDate = moment().format('YYYY-MM-DD');

const defaultRequestData = {
  start: thisMonthStartDay,
  end: thisMonthEndDate,
  sortBy: 'date',
  sort: 'asc',
  page: 1,
  limit: 15,
};

const healthPlanLimit = 20;

const Pipeline = () => {
  const isAdmin = AccountHelpers.isInRole('Admin');
  const isHealth = AccountHelpers.isInRole('Health');

  const { query, setQuery } = useQuery();

  const dispatch = useDispatch();
  const { selectHealthList, healthTotalPage, healthCurrentPage } = useSelector((store) => store.healths);

  const [pipelineData, setPipelineData] = useState({ list: [], totalPage: 1 });
  const [loading, setLoading] = useState('firstLoad');
  const [memberModalData, setMemberModalData] = useState(null);
  const [deletingRecord, setDeletingRecord] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const [requestData, setRequestData] = useState({
    start: query.start || thisMonthStartDay,
    end: query.start && !query.end ? '' : query.end || thisMonthEndDate,
    sortBy: query.sortBy || 'date',
    sort: query.sort || 'asc',
    page: query.page || 1,
    limit: query.limit || 15,
    hub: query.hub || '',
  });

  const {
    page, limit, sort, sortBy, hub,
  } = requestData;

  const getPipeline = async (params, loadingType = 'page') => {
    setLoading(loadingType);

    try {
      const { data } = await Api.getIehp(Utils.deleteEmptyKeys(params));

      setPipelineData({
        list: (data?.data?.response || []).map((d) => ({
          ...d,
          member: [d.Patient?.firstName, d.Patient?.lastName].filter(Boolean).join(' '),
        })),
        totalPage: data?.data?._meta?.pageCount || 1,
      });

      if (loadingType !== 'firstLoad') setQuery(params);
      setLoading('');
    } catch (e) {
      console.warn(e);
    }
  };

  useEffect(() => {
    (async () => {
      const newRequestData = { ...requestData };

      if (isAdmin) {
        const { payload } = await dispatch(getHealthRequest({ limit: healthPlanLimit }));

        if (!newRequestData.hub) {
          newRequestData.hub = payload?.data?.data?.response?.[0]?.hubId || '';

          setRequestData(newRequestData);
        }
      }

      await getPipeline(collectRequestData(newRequestData), 'firstLoad');
    })();
  }, []);

  const nextHealthPlanOptionListRequest = async (nextPage) => {
    await dispatch(getHealthRequest({ page: nextPage, limit: healthPlanLimit }));
  };

  const onChangeHub = async (option) => {
    const newRequestData = { ...requestData, hub: option, page: 1 };

    setRequestData(newRequestData);

    await getPipeline(collectRequestData(newRequestData));
  };

  const onChangeDate = async (currentDate) => {
    const newRequestData = {
      ...requestData, start: currentDate[0] || '', end: currentDate[1] || '', page: 1,
    };

    setRequestData(newRequestData);

    await getPipeline(collectRequestData(newRequestData), 'filter');
  };

  const onSortBy = async ({ sort: newSort, sortBy: newSortBy }) => {
    const newRequestData = {
      ...requestData, sort: newSort, sortBy: newSortBy, page: 1,
    };

    setRequestData(newRequestData);

    await getPipeline(collectRequestData(newRequestData));
  };

  const onChangePageData = async (path, count) => {
    const newRequestData = { ...requestData, [path]: count };

    if (path === 'limit') newRequestData.page = 1;

    setRequestData(newRequestData);

    await getPipeline(collectRequestData(newRequestData));
  };

  const clearFilters = async () => {
    const newRequestData = { ...defaultRequestData, hub };

    setRequestData(newRequestData);

    await getPipeline(collectRequestData(newRequestData), 'filter');
  };

  const tableData = useMemo(() => pipelineData.list.map((d) => ({
    ...d,
    date: formattedDate(d.date),
    dob: formattedDate(d.Patient?.dob),
    followUpDate: formattedDate(d.followUpDate),
    finalOutcomeDate: formattedDate(d.finalOutcomeDate),
  })), [pipelineData.list]);

  const columns = useMemo(() => [
    ...tableHeader,
    {
      name: 'Actions',
      path: 'actions',
      render: (d) => (
        <div className={classes.actions_cell}>
          <button
            type="button"
            className={classes.edit_btn}
            onClick={() => setMemberModalData(d)}
          >
            <EditIcon />
          </button>

          <button
            type="button"
            onClick={() => setDeletingRecord(d)}
          >
            <DeleteIcon />
          </button>
        </div>
      ),
    },
  ], []);

  const downloadPipeline = async () => {
    setDownloadLoading(true);

    try {
      await Api.getIehp({ ...collectRequestData(requestData), download: true });
    } catch (e) {
      console.warn(e);

      toast.error(e.response?.data?.message || 'Something went wrong');
    }

    setDownloadLoading(false);
  };

  const onSaved = async () => {
    await getPipeline(collectRequestData(requestData));

    setMemberModalData(null);
  };

  const deleteRecord = async () => {
    setDeleteLoading(true);

    try {
      await Api.deleteIehp({ id: deletingRecord.id });

      toast.success('Record successfully deleted');

      setDeletingRecord(null);

      const newRequestData = pipelineData.list.length === 1 && +requestData.page > 1
        ? { ...requestData, page: +requestData.page - 1 }
        : requestData;

      setRequestData(newRequestData);

      await getPipeline(collectRequestData(newRequestData));
    } catch (e) {
      console.warn(e);

      toast.error(e.response?.data?.message || 'Something went wrong');
    }

    setDeleteLoading(false);
  };

  const deletingMemberName = [deletingRecord?.Patient?.firstName, deletingRecord?.Patient?.lastName]
    .filter(Boolean).join(' ');

  return (
    <div className={classes.wrapper}>
      <div className={classes.page_header}>
        <h2>Pipeline</h2>

        <div className={classes.header_btns_wrapper}>
          <div className={classes.download_btn_wrapper}>
            <Button
              title="Download"
              downloadButton
              loading={downloadLoading}
              onClick={downloadPipeline}
            />
          </div>

          {isHealth && (
            <div className={classes.add_btn_wrapper}>
              <Button
                title="Add member"
                reverseColor
                addButton
                onClick={() => setMemberModalData({})}
              />
            </div>
          )}
        </div>
      </div>

      <div className={classes.filters_wrapper}>
        <RangeDatePicker
          currentDate={requestData}
          onChangeDate={onChangeDate}
          showLastYear
          emptyAfterClear
        />

        {isAdmin && (
          <div className={classes.select_wrapper}>
            <Select
              label="All health plans"
              nextOptionListRequest={nextHealthPlanOptionListRequest}
              options={selectHealthList}
              totalPage={healthTotalPage}
              currentPage={healthCurrentPage}
              limit={healthPlanLimit}
              currentOption={hub}
              onChange={onChangeHub}
              optionPathname="hubId"
              hideClearButton
            />
          </div>
        )}

        <div className={classes.clear_btn_wrapper}>
          <Button
            title="Clear"
            clearButton
            onClick={clearFilters}
          />
        </div>
      </div>

      <div className={classes.iehp_table_wrapper}>
        {loading === 'firstLoad'
          ? <Loader />

          : (
            <TableAndPagination
              onChange={onChangePageData}
              data={tableData}
              header={columns}
              loading={loading === 'page' || loading === 'filter'}
              sort={sort}
              sortBy={sortBy}
              onSortBy={onSortBy}
              totalPage={pipelineData.totalPage}
              page={+page - 1}
              limit={+limit}
            />
          )}
      </div>

      <Modal
        open={!!memberModalData}
        onClose={() => setMemberModalData(null)}
        className={classes.iehp_modal}
      >
        <MemberModalContent
          data={memberModalData || {}}
          onCancel={() => setMemberModalData(null)}
          onSaved={onSaved}
        />
      </Modal>

      <AttentionModal
        isOpen={!!deletingRecord}
        onClose={() => setDeletingRecord(null)}
        onSave={deleteRecord}
        loading={deleteLoading}
        title="Delete this member?"
        text={deletingRecord ? `${deletingMemberName} - ${formattedDate(deletingRecord.date)}` : ''}
      />
    </div>
  );
};

export default Pipeline;
